import React, { useState, useEffect, useRef, useMemo, SyntheticEvent, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import './SendPage.css';
import SendPageHeader from './components/SendPageHeader';
import { SetInfo } from 'store/services/sets/setsApi';
import CardPrintingsContainer from 'components/CardPrintingsContainer/CardPrintingsContainer';
import CardGroupContainerResponsive from 'components/CardGroupContainer/CardGroupContainerResponsive';
import InitiateTradeModal, { TradeCard } from './components/InitiateTradeModal';
import {
  useGetAvailablePackagesQuery,
  useLazyGetAvailablePackagesQuery,
  useInitiateTradeMutation,
  TradePackage,
  TradeCard as ApiTradeCard,
  useCreateTradeLockMutation,
  useReleaseTradeLockMutation,
  tradeApi,
  useGetMatchIndexQuery,
  useTrackCsvExportMutation,
} from 'store/services/trade/tradeApi';
import { RootState } from 'store';
import { Points } from 'components/Points/Points';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { meApi, useGetMeQuery } from 'store/services/user/meApi';
import { useHaveListQuantitySummaryQuery } from 'store/services/collectionList/haveList/haveListApi';
import { useResendVerificationMutation } from 'store/services/auth/authApi';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { getSetCode, isUUID } from 'utils/setMappings';
import { FoilCardImage } from 'components/FoilCardImage';
import {
  expandAutoMatchCards,
  resolveAutoMatchImageUrl,
  buildHaveMatchMapFromPackage,
  buildModalTradeCard,
  AutoMatchedCard,
  HaveMatchEntry,
} from './utils/autoMatchExpansion';
import SendCardTooltip from './components/SendCardTooltip';
import Pagination from 'components/Pagination/Pagination';
import DomesticTradesBanner from './components/DomesticTradesBanner';
import { SendListView } from './components/SendListView';
import type { ProcessedPackage } from './components/SendListView/SendListView';
import { generateExplodedCsvRows, formatCsvContent, downloadCsv, ROW_LIMIT } from './utils/csvExport';
import { trackEvent } from 'utils/analytics';
import { useTraderBalanceSocket } from 'hooks/useTraderBalanceSocket';

const PLACEHOLDER_IMAGE = AssetUrls.search.imageComing;

// Helper function to clean malformed URLs from backend
const cleanImageUrl = (url: string | null | undefined): string => {
  if (!url) return AssetUrls.profile.defaultAvatar;

  // If URL contains the error pattern, strip it
  if (url.includes('{failed')) {
    const cleanUrl = url.split('{failed')[0].trim();
    return cleanUrl || AssetUrls.profile.defaultAvatar;
  }

  return url;
};

export default function SendPage2() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<TradePackage | null>(null);
  const [displayMode, setDisplayMode] = useState<'comfortable' | 'compact' | 'list'>('comfortable');
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [currentLockId, setCurrentLockId] = useState<string | null>(null);
  const [currentLockToken, setCurrentLockToken] = useState<string | null>(null);
  const [lockExpiresAt, setLockExpiresAt] = useState<string | null>(null);
  // Initialize search from URL params if present
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [searchType, setSearchType] = useState<'card' | 'user'>((searchParams.get('searchType') as 'card' | 'user') || 'card');
  // Pagination state
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [autoAdvanceCount, setAutoAdvanceCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Price filter state - initialize from URL params
  const [priceMin, setPriceMin] = useState<number | undefined>(() => {
    const param = searchParams.get('priceMin');
    return param ? parseInt(param, 10) : undefined;
  });
  const [priceMax, setPriceMax] = useState<number | undefined>(() => {
    const param = searchParams.get('priceMax');
    return param ? parseInt(param, 10) : undefined;
  });
  const [debouncedPriceMin, setDebouncedPriceMin] = useState<number | undefined>(priceMin);
  const [debouncedPriceMax, setDebouncedPriceMax] = useState<number | undefined>(priceMax);
  // Set filter state
  const [selectedSets, setSelectedSets] = useState<SetInfo[]>([]);
  // Sort state
  const [sortValue, setSortValue] = useState<string>('default');
  const sortParams = useMemo(() => {
    if (sortValue === 'default') return {};
    const [sort, direction] = sortValue.split('-');
    return { sort, direction: direction as 'asc' | 'desc' };
  }, [sortValue]);
  // Track user-initiated fetches (pagination/search) vs background polling
  const [isUserInitiatedFetch, setIsUserInitiatedFetch] = useState(false);
  // Track sort changes specifically — drives the full-page loader (vs the in-place pagination overlay)
  const [isSortChanging, setIsSortChanging] = useState(false);
  // Track selected cards per package (packageUserId -> Set of cardIds)
  const [selectedCardsPerPackage, setSelectedCardsPerPackage] = useState<Map<string, Set<string>>>(new Map());
  const [removedPackages, setRemovedPackages] = useState<Set<string>>(new Set());
  const [loadingMessage, setLoadingMessage] = useState('Loading available packages...');
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingTimer2Ref = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch current user once — used by multiple memos below (prefer-domestic sort, etc.)
  const { data: meData, refetch: refetchUser } = useGetMeQuery();

  // Auto-Match State Management
  const [globalAutoMatch, setGlobalAutoMatch] = useState<boolean>(() => {
    try {
      return localStorage.getItem('sendPage_globalAutoMatch') === 'true';
    } catch {
      return false;
    }
  });

  // Per-package auto-match states (session only - not persisted)
  const [packageAutoMatchStates, setPackageAutoMatchStates] = useState<Map<string, boolean>>(new Map());

  // Hover tooltip state
  const [hoveredCardData, setHoveredCardData] = useState<{
    cardId: string;
    position: { x: number; y: number };
    data: ApiTradeCard;
    username: string;
  } | null>(null);
  const [hoveredSetCode, setHoveredSetCode] = useState<string | null>(null);

  // Redux state for rate limiting and maintenance mode
  const isRateLimited = useSelector((state: RootState) => state.rateLimit.isRateLimited);
  const isMaintenanceMode = useSelector((state: RootState) => state.maintenance.isMaintenanceModeActive);

  // Calculate polling interval (pause if modal open, rate limited, or maintenance mode)
  const pollingInterval = (isModalOpen || isRateLimited || isMaintenanceMode) ? 0 : 15000;

  // Fetch match index for auto-match feature
  const {
    data: matchIndexData,
    isLoading: isMatchIndexLoading,
    refetch: refetchMatchIndex,
  } = useGetMatchIndexQuery();

  // Fetch sender's have list quantity summary for "You Own" column
  const { data: quantitySummaryData } = useHaveListQuantitySummaryQuery();
  const haveListQuantityMap = useMemo(() => {
    const map = new Map<string, number>();
    if (quantitySummaryData?.quantities) {
      for (const [name, qty] of Object.entries(quantitySummaryData.quantities)) {
        map.set(name, qty);
      }
    }
    return map;
  }, [quantitySummaryData]);
  const haveListQuantityByCardIdMap = useMemo(() => {
    const map = new Map<number, number>();
    if (quantitySummaryData?.quantitiesByCardId) {
      for (const [cardId, qty] of Object.entries(quantitySummaryData.quantitiesByCardId)) {
        map.set(Number(cardId), qty);
      }
    }
    return map;
  }, [quantitySummaryData]);

  // Derive matching user IDs for server-side filtering when global auto-match is ON
  const matchingUserIds = useMemo(() => {
    if (!globalAutoMatch) return undefined;
    if (!matchIndexData || matchIndexData.error) return undefined;
    const ids = Object.keys(matchIndexData.packages || {});
    if (ids.length === 0) return undefined;
    // Safety valve: skip server-side filtering if too many IDs to avoid URL length limits
    if (ids.length > 200) return undefined;
    return ids.join(',');
  }, [globalAutoMatch, matchIndexData]);

  // Skip fetching packages until match index is ready when auto-match is on
  // This prevents fetching ALL users unfiltered during the brief window before match index loads
  const shouldSkipPackagesQuery = globalAutoMatch && isMatchIndexLoading;

  // Fetch available packages from the API
  const { data: rawData, isLoading, error, isFetching } = useGetAvailablePackagesQuery(
    {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      search: debouncedSearchTerm.trim() || undefined,
      searchType: debouncedSearchTerm.trim() ? searchType : undefined,
      priceMin: debouncedPriceMin,
      priceMax: debouncedPriceMax,
      userIds: matchingUserIds,
      sort: sortParams.sort,
      direction: sortParams.direction,
      setCodes: selectedSets.length > 0 ? selectedSets.map(s => s.code).join(',') : undefined,
    },
    {
      pollingInterval, // Poll every 15 seconds (or pause if conditions met)
      skip: shouldSkipPackagesQuery,
    }
  );

  // Elite "Prefer Domestic Trades": re-sort so same-country traders appear first.
  // Within each partition, preserves the server's existing sort order.
  const data = useMemo(() => {
    if (!rawData?.packages) return rawData;
    const userCountry = meData?.country;
    if (!meData?.preferDomesticTrades || !userCountry) return rawData;
    const domestic: typeof rawData.packages = [];
    const international: typeof rawData.packages = [];
    for (const pkg of rawData.packages) {
      if (pkg.country === userCountry) {
        domestic.push(pkg);
      } else {
        international.push(pkg);
      }
    }
    if (domestic.length === 0) return rawData;
    return { ...rawData, packages: [...domestic, ...international] };
  }, [rawData, meData?.preferDomesticTrades, meData?.country]);

  // Debug authentication state on staging
  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'staging') {
      console.log('🔐 Staging Auth Debug:', {
        isAuthenticated: !!localStorage.getItem('user'),
        hasCookies: document.cookie.length > 0,
        cookieList: document.cookie,
        apiUrl: process.env.REACT_APP_API_URL,
        cookieAuth: process.env.REACT_APP_USE_COOKIE_AUTH
      });

      if (error) {
        console.error('📦 Package fetch error:', error);
      }
    }
  }, [error]);

  // Subscribe to real-time balance updates for visible traders
  const traderIds = useMemo(
    () => Array.from(new Set((data?.packages || []).map(pkg => pkg.userId))),
    [data?.packages]
  );
  useTraderBalanceSocket({ traderIds, enabled: traderIds.length > 0 });

  // Keep selectedPackage.pointBalance in sync with fresh cache data
  useEffect(() => {
    if (data?.packages) {
      setSelectedPackage(prev => {
        if (!prev) return null;
        const freshPkg = data.packages.find(p => p.userId === prev.userId);
        if (freshPkg && freshPkg.pointBalance !== prev.pointBalance) {
          return { ...prev, pointBalance: freshPkg.pointBalance };
        }
        return prev;
      });
    }
  }, [data?.packages]);

  const [initiateTrade, { isLoading: isInitiatingTrade }] = useInitiateTradeMutation();
  const [createTradeLock] = useCreateTradeLockMutation();
  const [releaseTradeLock] = useReleaseTradeLockMutation();
  const [resendVerification] = useResendVerificationMutation();
  const [calculatePrice] = useCalculatePriceMutation();

  // State for auto-match printing-specific resolved prices
  const [autoMatchPrices, setAutoMatchPrices] = useState<Map<string, number>>(new Map());

  // CSV Export
  const [isExporting, setIsExporting] = useState(false);
  const [fetchAllPackages] = useLazyGetAvailablePackagesQuery();
  const [trackCsvExport] = useTrackCsvExportMutation();

  // We now use comprehensive static mappings from utils/setMappings.ts
  // No need to fetch from backend anymore

  // Only show loading state on initial load, not when refetching cached data
  // Also show loading when auto-match is on and we're waiting for match index
  // Also re-fire on sort change so users get the same full-page feedback as initial load
  const showLoadingState = (isLoading && !data) || shouldSkipPackagesQuery || isSortChanging;

  // Show loading overlay only during user-initiated pagination/search, not background polling
  const showPaginationLoading = isFetching && !!data && isUserInitiatedFetch;

  // Timer to update loading message progressively
  useEffect(() => {
    if (showLoadingState) {
      // Reset message when loading starts
      setLoadingMessage('Loading available packages...');

      // Set first timer to change message after 4 seconds
      loadingTimerRef.current = setTimeout(() => {
        setLoadingMessage('Building initial cache to improve future performance...');
      }, 4000);

      // Set second timer to change message after 10 seconds
      loadingTimer2Ref.current = setTimeout(() => {
        setLoadingMessage('Almost done...');
      }, 10000);
    } else {
      // Clear timers when loading stops
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (loadingTimer2Ref.current) {
        clearTimeout(loadingTimer2Ref.current);
        loadingTimer2Ref.current = null;
      }
      // Reset message for next load
      setLoadingMessage('Loading available packages...');
    }

    // Cleanup on unmount
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      if (loadingTimer2Ref.current) {
        clearTimeout(loadingTimer2Ref.current);
      }
    };
  }, [showLoadingState])

  // Persist global auto-match state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sendPage_globalAutoMatch', String(globalAutoMatch));
    } catch (error) {
      console.warn('Failed to persist auto-match state:', error);
    }
  }, [globalAutoMatch]);

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== debouncedSearchTerm) {
        setIsUserInitiatedFetch(true); // Mark as user-initiated when search changes
      }
      setDebouncedSearchTerm(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearchTerm]);

  // Debounce price filters (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (priceMin !== debouncedPriceMin || priceMax !== debouncedPriceMax) {
        setIsUserInitiatedFetch(true);
      }
      setDebouncedPriceMin(priceMin);
      setDebouncedPriceMax(priceMax);
    }, 300);
    return () => clearTimeout(timer);
  }, [priceMin, priceMax, debouncedPriceMin, debouncedPriceMax]);

  // Sync filters to URL params
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (priceMin && priceMin > 0) newParams.set('priceMin', String(priceMin));
    else newParams.delete('priceMin');
    if (priceMax && priceMax > 0) newParams.set('priceMax', String(priceMax));
    else newParams.delete('priceMax');
    if (searchType !== 'card') newParams.set('searchType', searchType);
    else newParams.delete('searchType');
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [priceMin, priceMax, searchType, searchParams, setSearchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, debouncedPriceMin, debouncedPriceMax, matchingUserIds, sortValue, searchType, selectedSets]);

  // Reset user-initiated fetch flag when fetching completes
  useEffect(() => {
    if (!isFetching && isUserInitiatedFetch) {
      setIsUserInitiatedFetch(false);
    }
  }, [isFetching, isUserInitiatedFetch]);

  // Reset sort-changing flag when fetch completes so the full-page loader clears
  useEffect(() => {
    if (!isFetching && isSortChanging) {
      setIsSortChanging(false);
    }
  }, [isFetching, isSortChanging]);

  // Sort change triggers the full-page loader (matches initial-load UX). isUserInitiatedFetch
  // is also flipped so the dropdown's spinner state in the loading-branch header still fires.
  const handleSortChange = useCallback((next: string) => {
    setSortValue(prev => {
      if (prev === next) return prev;
      setIsSortChanging(true);
      setIsUserInitiatedFetch(true);
      return next;
    });
  }, []);

  const isSortLoading = isFetching && !!data && isUserInitiatedFetch;

  // Auto-Match handler functions
  const handleGlobalAutoMatchToggle = (enabled: boolean) => {
    setGlobalAutoMatch(enabled);
  };

  const handlePackageAutoMatchToggle = (packageUserId: number, enabled: boolean) => {
    // Only allow when global is OFF
    if (globalAutoMatch) return;

    const packageKey = `pkg-${packageUserId}`;
    setPackageAutoMatchStates(prev => {
      const next = new Map(prev);
      next.set(packageKey, enabled);
      return next;
    });
  };

  // CSV Export handler
  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const result = await fetchAllPackages({
        page: 1,
        limit: 500,
        forExport: true,
        search: debouncedSearchTerm.trim() || undefined,
        priceMin: debouncedPriceMin,
        priceMax: debouncedPriceMax,
        userIds: matchingUserIds,
      }).unwrap();

      if (!result.packages || result.packages.length === 0) {
        toast.info('No packages to export.');
        return;
      }

      const { rows, truncated } = generateExplodedCsvRows(result.packages);

      if (rows.length === 0) {
        toast.info('No exportable card data found in the filtered results.');
        return;
      }

      if (truncated) {
        toast.warn(
          `Export limited to ${ROW_LIMIT.toLocaleString()} rows. Consider narrowing your filters.`,
          { autoClose: 6000 }
        );
      }

      const csvContent = formatCsvContent(rows);
      const date = new Date().toISOString().split('T')[0];
      downloadCsv(csvContent, `trademagic-wants-${date}.csv`);

      trackEvent('csv_export', {
        total_packages: result.packages.length,
        total_rows: rows.length,
        was_truncated: truncated,
        had_search_filter: !!debouncedSearchTerm.trim(),
        had_price_filter: !!(debouncedPriceMin || debouncedPriceMax),
      });

      // Fire-and-forget backend analytics
      trackCsvExport().catch(() => {});

      toast.success(`Exported ${rows.length.toLocaleString()} rows to CSV.`);
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Failed to export CSV. Please try again.');
      trackEvent('csv_export_error', { error: String(err) });
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllPackages, trackCsvExport, debouncedSearchTerm, debouncedPriceMin, debouncedPriceMax, matchingUserIds]);

  // Get effective auto-match state for a package
  const getPackageAutoMatchState = useCallback((packageUserId: number): boolean => {
    if (globalAutoMatch) return true;
    const packageKey = `pkg-${packageUserId}`;
    return packageAutoMatchStates.get(packageKey) ?? false;
  }, [globalAutoMatch, packageAutoMatchStates]);

  // Resolve sender's have-list image for a card when auto-match is active
  const getAutoMatchImageUrl = useCallback((
    card: ApiTradeCard,
    haveMatchMap?: Map<string, HaveMatchEntry>
  ): string | undefined => {
    return resolveAutoMatchImageUrl(card as AutoMatchedCard, haveMatchMap);
  }, []);

  // Build a map of wantListId → sender's have list match info from the match index
  // Used by getCurrentCards() to default set/condition/foil to the sender's actual card
  const buildHaveMatchMap = useCallback((userId: number): Map<string, HaveMatchEntry> => {
    if (!matchIndexData || matchIndexData.error) return new Map();
    const packageMatch = matchIndexData.packages?.[String(userId)];
    return buildHaveMatchMapFromPackage(packageMatch);
  }, [matchIndexData]);

  // Calculate match summary for header display
  const matchSummary = React.useMemo(() => {
    if (!matchIndexData || matchIndexData.error) return undefined;

    return {
      matchingCards: matchIndexData.totalMatchingCards,
      totalCards: data?.packages?.reduce((sum, pkg) => sum + pkg.cards.length, 0) || 0,
      matchingPackages: matchIndexData.totalMatchingPackages,
      totalPackages: data?.packages?.length || 0,
    };
  }, [matchIndexData, data?.packages]);

  const handleSendPackage = async (tradePackage: TradePackage) => {
    // Filter cards to only include selected ones
    const packageKey = `pkg-${tradePackage.userId}`;
    const selectedCards = selectedCardsPerPackage.get(packageKey);

    // If no specific selection, default to all cards selected
    const isAllSelected = !selectedCards || selectedCards.size === 0;

    let filteredCards: ApiTradeCard[] = [];

    if (isAllSelected && !removedPackages.has(packageKey)) {
      // All cards are selected (default state)
      filteredCards = tradePackage.cards;
    } else if (removedPackages.has(packageKey)) {
      // All cards removed - shouldn't send anything
      toast.warning('Please select at least one card to send');
      return;
    } else {
      // Some cards are selected
      tradePackage.cards.forEach(card => {
        let selectedQuantity = 0;
        for (let i = 0; i < card.quantity; i++) {
          const cardKey = `${card.id}-${i}`;
          if (selectedCards?.has(cardKey)) {
            selectedQuantity++;
          }
        }
        if (selectedQuantity > 0) {
          filteredCards.push({ ...card, quantity: selectedQuantity });
        }
      });

      if (filteredCards.length === 0) {
        toast.warning('Please select at least one card to send');
        return;
      }
    }

    // Update the package with filtered cards
    const packageToSend = { ...tradePackage, cards: filteredCards };

    // Pre-flight check: Validate authentication before attempting lock
    try {
      console.log('🔐 Performing pre-flight authentication check...');
      const result = await refetchUser();
      if (result.error) {
        throw result.error;
      }
      console.log('✅ Authentication validated');

      // Check email verification status
      if (!result.data?.emailVerified) {
        console.log('❌ Email not verified');
        // Show toast with resend button
        toast.warning(
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Please verify your email address to send cards. Check your inbox for the verification link.</span>
            <button
              onClick={async () => {
                try {
                  if (!result.data?.email) {
                    toast.error('Unable to send verification email. Please try again.');
                    return;
                  }
                  await resendVerification({ email: result.data.email }).unwrap();
                  toast.success('Verification email sent! Please check your inbox.');
                } catch (error) {
                  toast.error('Failed to send verification email. Please try again.');
                }
              }}
              style={{
                padding: '4px 8px',
                background: '#7C3AED',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              Resend
            </button>
          </div>,
          {
            autoClose: false,
            closeButton: true
          }
        );
        return;
      }
    } catch (authError) {
      console.error('❌ Pre-flight authentication check failed:', authError);
      toast.error('Please refresh the page to restore your session');
      // Force refresh user data
      dispatch(meApi.util.invalidateTags(['User']));
      return;
    }

    // Retry logic for cookie authentication issues
    let retryCount = 0;
    const maxRetries = 2;
    const retryDelay = 500; // ms

    while (retryCount <= maxRetries) {
      try {
        // Log attempt for debugging
        if (retryCount > 0) {
          console.log(`🔄 Retry attempt ${retryCount} for trade lock creation`);
        }

        // Try to create a lock for this receiver
        const lockResult = await createTradeLock({ receiverId: tradePackage.userId }).unwrap();

        if (lockResult.success) {
          setCurrentLockId(lockResult.lockId || null);
          setCurrentLockToken(lockResult.lockToken || null);
          setLockExpiresAt(lockResult.expiresAt || null);
          setSelectedPackage(packageToSend); // Use the filtered package
          setIsModalOpen(true);
          return; // Success - exit function
        } else {
          // Someone else has locked this receiver
          toast.error(lockResult.message || 'Another sender is building a trade for this receiver. Please check back soon.');
          return; // Exit - no retry needed for business logic errors
        }
      } catch (error: any) {
        console.error(`Failed to create trade lock (attempt ${retryCount + 1}):`, error);
        console.error('Lock creation error details:', {
          status: error.status,
          message: error.data?.message,
          error: error.data?.error,
          details: error.data?.details,
          receiverId: tradePackage.userId,
          attempt: retryCount + 1
        });

        // Handle case where lock endpoint returns an error
        if (error.status === 423 || error.data?.message?.includes('already locked')) {
          toast.error('This package is temporarily unavailable. Another sender is currently building a trade for this receiver. Please try again in a few minutes.');
          return; // Exit - no retry for business logic errors
        } else if (error.status === 409 || error.data?.message?.includes('already have a lock')) {
          toast.error('You already have another package open for trading. Please complete or cancel your current trade first.');
          return; // Exit - no retry for business logic errors
        } else if (error.status === 401 || error.status === 403) {
          // Authentication issue - likely cookie not set after signup or expired
          console.error('Authentication failed when creating lock - cookie issue detected');

          if (retryCount < maxRetries) {
            // Wait and retry - cookie might need time to propagate
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue; // Retry the request
          } else {
            // All retries exhausted
            toast.error('Your session has expired. Please refresh the page to continue trading.');

            // Force a user data refetch to potentially refresh the session
            dispatch(meApi.util.invalidateTags(['User']));
            return;
          }
        } else if (error.status === 400) {
          // 400 can mean various issues - parse the backend message
          let errorMessage = 'Unable to reserve this package. ';

          const backendMessage = error.data?.message || error.data?.error || '';
          if (backendMessage.includes('already have a lock') || backendMessage.includes('existing lock')) {
            errorMessage = 'You already have another package open for trading. Please complete or cancel your current trade first.';
          } else if (backendMessage.includes('trade limit')) {
            errorMessage = 'The receiver has reached their maximum number of active trades. Please try again later.';
          } else if (backendMessage.includes('package') && backendMessage.includes('not found')) {
            errorMessage = 'This package is no longer available. It may have been traded or removed.';
          } else if (backendMessage) {
            errorMessage = backendMessage; // Use backend message if specific
          } else {
            errorMessage += 'The package may no longer be available.';
          }

          toast.error(errorMessage);
          return; // Exit - no retry for bad requests
        } else if (error.status === 500) {
          // Server error
          toast.error('The trade service is temporarily unavailable. Please try again in a moment.');
          return;
        } else if (error.status === 0 || error.code === 'ERR_NETWORK') {
          // Network error
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue;
          } else {
            toast.error('Unable to connect to the trade server. Please check your internet connection.');
            return;
          }
        } else {
          // Other errors - retry might help
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            retryCount++;
            continue;
          } else {
            // Lock is required - do not proceed without it
            toast.error('Unable to reserve this package for trading. Please try again or contact support if the problem persists.');
            return;
          }
        }
      }
    }
  };

  const handleCloseModal = async () => {
    // Release the lock if we have one
    if (currentLockToken && selectedPackage) {
      try {
        await releaseTradeLock({
          receiverId: selectedPackage.userId,
          lockToken: currentLockToken
        }).unwrap();

        // Force refresh of available packages to show the unlocked package
        dispatch(tradeApi.util.invalidateTags(['TradePackages']));
      } catch (error: any) {
        console.error('Failed to release trade lock:', error);
        // Silent failure - lock will expire automatically after 5 minutes
      }
    }

    // Clear state regardless of lock release success
    setIsModalOpen(false);
    setSelectedPackage(null);
    setCurrentLockId(null);
    setCurrentLockToken(null);
    setLockExpiresAt(null);
  };

  const handleInitiateTrade = async (cards: TradeCard[]) => {
    if (!selectedPackage) {
      toast.error('No package selected');
      return;
    }

    // Validate that no cards have 0 value
    const hasZeroValueCards = cards.some(card => {
      const cardValue = card.value || 0;
      return cardValue <= 0;
    });

    if (hasZeroValueCards) {
      toast.error('Cannot initiate trade: One or more cards have 0 points. Please remove cards with no value.');
      return;
    }

    // Calculate total value to ensure it's not 0
    const totalValue = cards.reduce((sum, card) => sum + (card.value || 0), 0);

    if (totalValue <= 0) {
      toast.error('Cannot initiate trade: Total trade value must be greater than 0 points.');
      return;
    }

    // All price validation happens server-side
    // Frontend prices are for display only

    try {
      // Map the cards from modal format to API format
      // Each row is an individual copy with quantity: 1
      const apiCards = cards.map(card => {
        // Look up original package card by wantListId (card.id is now a unique row ID).
        // For auto-match collapsed cards, the modal row's wantListId comes from one
        // of the card's _autoMatchAssignments and may not equal the collapsed card's
        // representative wantListId — fall through to assignment lookup in that case.
        const originalCard =
          selectedPackage.cards.find(c => c.wantListId === card.wantListId)
          ?? selectedPackage.cards.find(c =>
            (c as AutoMatchedCard)._autoMatchAssignments?.some(a => a.wantListId === card.wantListId)
          );
        if (!originalCard) {
          throw new Error(`Card ${card.name} not found in package`);
        }

        // Debug logging for cardId tracking
        console.log(`Card ${card.name} - Set: ${card.set}`, {
          originalCardId: originalCard.cardId,
          modalCardId: (card as any).cardId,
          selectedSetCode: card.setCode,
          wantListId: originalCard.wantListId,
          acceptedPrintingIds: (originalCard as any).acceptedPrintingIds
        });

        // Use the cardId from the modal if it was updated (when user changed set)
        // Otherwise fall back to the original cardId
        const finalCardId = card.cardId || originalCard.cardId;

        return {
          wantListId: originalCard.wantListId,
          cardId: finalCardId?.toString(),  // Include specific card ID for exact version matching
          quantity: 1,  // Each row is an individual copy
          condition: card.condition,
          language: card.language,
          foil: card.foil,
          edition: card.edition || undefined,
          set: card.setCode,  // Use setCode for API calls, not set name
        };
      });

      // Debug log the payload being sent
      console.log('Trade initiation payload:', {
        receiverId: selectedPackage.userId,
        cards: apiCards.map(c => ({ ...c, cardName: cards.find(card => card.id === selectedPackage.cards.find(oc => oc.wantListId === c.wantListId)?.id)?.name }))
      });

      const result = await initiateTrade({
        receiverId: selectedPackage.userId,
        lockToken: currentLockToken || undefined,
        cards: apiCards,
      }).unwrap();

      if (result.success) {
        toast.success('Trade initiated successfully!');

        // Clear lock state immediately - backend auto-releases lock on successful trade
        // This prevents handleCloseModal from attempting a redundant release
        setCurrentLockToken(null);
        setCurrentLockId(null);
        setLockExpiresAt(null);

        // Force refresh packages after successful trade
        dispatch(tradeApi.util.invalidateTags(['TradePackages']));

        // Close modal and clear remaining state
        setIsModalOpen(false);
        setSelectedPackage(null);

        // Redirect to /sending page to see the new trade
        navigate('/sending');
      } else {
        toast.error(result.message || 'Failed to initiate trade');
      }
    } catch (error: any) {
      console.error('Failed to initiate trade:', error);
      console.error('Trade initiation error details:', {
        status: error.status,
        message: error.data?.message,
        error: error.data?.error,
        details: error.data?.details,
        cards: cards.map(c => ({ name: c.name, qty: c.qty, set: c.set })),
        receiverId: selectedPackage.userId
      });

      // Handle specific error cases from backend
      const errorMessage = error.data?.message || error.data?.error || error.message || '';

      // Parse error message for specific scenarios
      if (errorMessage.includes('Insufficient points')) {
        // Try to extract how many points are needed
        const pointsMatch = errorMessage.match(/needs? (\d+) (?:more )?points?/i);
        if (pointsMatch) {
          toast.error(`The receiver doesn't have enough points to complete this trade. They need ${pointsMatch[1]} more points.`);
        } else {
          toast.error('The receiver doesn\'t have enough points to complete this trade.');
        }
      } else if (errorMessage.includes('not found in want list') || errorMessage.includes('no longer wanted')) {
        // Extract card name if available
        const cardMatch = errorMessage.match(/card "([^"]+)"/i) || errorMessage.match(/(\w+) (?:is |are )?not found/i);
        if (cardMatch) {
          toast.error(`"${cardMatch[1]}" is no longer in the receiver's want list. The package has been updated.`);
        } else {
          toast.error('Some cards are no longer in the receiver\'s want list. The package has been updated - please try again.');
        }
      } else if (errorMessage.includes('quantity exceeds') || errorMessage.includes('too many copies')) {
        // Extract card and quantity info
        const quantityMatch = errorMessage.match(/only wants? (\d+) (?:copies?|cards?).*?(?:of |for )?["']?([^"']+)["']?/i);
        if (quantityMatch) {
          toast.error(`The receiver only wants ${quantityMatch[1]} copies of "${quantityMatch[2]}". Please reduce the quantity.`);
        } else if (errorMessage.includes('already sent') || errorMessage.includes('already received')) {
          // Handle case where user already sent some copies
          const sentMatch = errorMessage.match(/already (?:sent|received) (\d+).*?wants? (\d+)/i);
          if (sentMatch) {
            toast.error(`You've already sent ${sentMatch[1]} copies to this user. They only want ${sentMatch[2]} total copies.`);
          } else {
            toast.error('You\'ve already sent some copies of this card to this user. They don\'t need more.');
          }
        } else {
          toast.error('The quantity exceeds what the receiver wants. Please check their want list.');
        }
      } else if (errorMessage.includes('shipping address') || errorMessage.includes('address required')) {
        toast.error('You must add a shipping address in Settings before initiating trades.');
      } else if (errorMessage.includes('User not found') || errorMessage.includes('user does not exist')) {
        toast.error('Unable to verify user information. Please log out and log back in.');
      } else if (errorMessage.includes('lock expired') || errorMessage.includes('lock invalid') || errorMessage.includes('Valid lock required')) {
        toast.error('Your package reservation has expired (5 minute limit). Please select the package again.');
      } else if (errorMessage.includes('not in acceptable sets') || errorMessage.includes('set not accepted')) {
        // Extract set and card info
        const setMatch = errorMessage.match(/(?:card |)["']?([^"']+)["']?.*?from (?:the |)["']?([^"']+)["']? set/i);
        if (setMatch) {
          toast.error(`The receiver doesn't accept "${setMatch[1]}" from the "${setMatch[2]}" set. Please choose a different version.`);
        } else {
          toast.error('The selected card version is not accepted by the receiver. Please choose a different set.');
        }
      } else if (errorMessage.includes('condition not acceptable') || errorMessage.includes('better condition')) {
        // Extract condition requirements
        const conditionMatch = errorMessage.match(/requires? (?:at least |minimum |)(\w+)/i);
        if (conditionMatch) {
          toast.error(`The receiver requires better condition for this card. Minimum condition: ${conditionMatch[1]}.`);
        } else {
          toast.error('The receiver requires better condition for this card.');
        }
      } else if (errorMessage.includes('duplicate trade') || errorMessage.includes('already initiated')) {
        toast.error('You\'ve already initiated a similar trade with this user. Please wait for it to complete before sending more.');
      } else if (errorMessage.includes('max trades') || errorMessage.includes('trade limit')) {
        toast.error('You\'ve reached the maximum number of active trades. Please complete some trades before starting new ones.');
      } else if (error.status === 500) {
        toast.error('The trade service encountered an error. Please try again in a moment.');
      } else if (error.status === 503) {
        toast.error('The trade service is temporarily unavailable. Please try again later.');
      } else if (error.status === 0 || error.code === 'ERR_NETWORK') {
        toast.error('Unable to connect to the trade server. Please check your internet connection.');
      } else if (errorMessage) {
        // Use backend error message if it's specific and not already handled
        toast.error(errorMessage);
      } else {
        // Generic fallback
        toast.error('Failed to initiate trade. Please try again or contact support if the problem persists.');
      }
    }
  };

  // Handle toggling individual card selection
  const handleToggleCard = (packageUserId: number, cardId: string) => {
    const packageKey = `pkg-${packageUserId}`;
    const currentSelected = selectedCardsPerPackage.get(packageKey);
    const isPackageRemoved = removedPackages.has(packageKey);
    let newSelected: Set<string>;

    if (currentSelected === undefined) {
      // First interaction with this package
      if (isPackageRemoved) {
        // First click after Remove All - select only this card
        newSelected = new Set<string>([cardId]);
        // Remove from removed packages since user is re-selecting
        const newRemoved = new Set(removedPackages);
        newRemoved.delete(packageKey);
        setRemovedPackages(newRemoved);
      } else {
        // Normal first click - select all cards except clicked one
        const pkg = data?.packages.find(p => p.userId === packageUserId);
        newSelected = new Set<string>();
        if (pkg) {
          pkg.cards.forEach(card => {
            for (let i = 0; i < card.quantity; i++) {
              const key = `${card.id}-${i}`;
              if (key !== cardId) { // Add all cards except the one being clicked
                newSelected.add(key);
              }
            }
          });
        }
      }
    } else {
      // Already interacted with package - just toggle the card
      newSelected = new Set(currentSelected);
      if (newSelected.has(cardId)) {
        newSelected.delete(cardId);
      } else {
        newSelected.add(cardId);
      }
    }

    const newMap = new Map(selectedCardsPerPackage);
    newMap.set(packageKey, newSelected);
    setSelectedCardsPerPackage(newMap);
  };

  // Handle card hover for tooltip display
  const handleCardMouseEnter = (
    e: React.MouseEvent<HTMLElement>,
    card: ApiTradeCard,
    username: string
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // For card grid items: account for hover transform scale(1.5) adding 25% to right side
    // For list view rows: no scale, use right edge directly
    const isGridCard = e.currentTarget.classList.contains('card-item');
    const rightEdge = isGridCard ? rect.right + (rect.width * 0.25) : rect.right + 8;
    setHoveredCardData({
      cardId: card.id,
      position: { x: rightEdge, y: rect.top },
      data: card,
      username,
    });
  };

  const handleCardMouseLeave = () => {
    setHoveredCardData(null);
    setHoveredSetCode(null);
  };

  // Calculate pagination values
  // When auto-match is on, use match index count (accurate count of matching users)
  // instead of API's totalPackages (which may include non-matching users)
  const totalPages = useMemo(() => {
    if (globalAutoMatch && matchIndexData && !matchIndexData.error) {
      const matchCount = matchIndexData.totalMatchingPackages || 0;
      return matchCount > 0 ? Math.ceil(matchCount / ITEMS_PER_PAGE) : 1;
    }
    return data?.totalPackages
      ? Math.ceil(data.totalPackages / ITEMS_PER_PAGE)
      : 1;
  }, [globalAutoMatch, matchIndexData, data?.totalPackages]);

  // Page change handler
  const handlePageChange = useCallback((newPage: number) => {
    setIsUserInitiatedFetch(true); // Mark as user-initiated when page changes
    setCurrentPage(newPage);
    // Scroll both containers to top (matches ScrollToTop pattern)
    const mainContent = document.querySelector('.main-page-content');
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, []);

  // Handle toggling all instances of a card (for compact mode)
  const handleToggleAllCardInstances = (packageUserId: number, cardId: string, quantity: number) => {
    const packageKey = `pkg-${packageUserId}`;
    const currentSelected = selectedCardsPerPackage.get(packageKey);
    const isPackageRemoved = removedPackages.has(packageKey);

    // Check current selection state of all instances
    let selectedCount = 0;
    if (isPackageRemoved) {
      selectedCount = 0;
    } else if (currentSelected === undefined) {
      // No interaction yet - default to all selected
      selectedCount = quantity;
    } else {
      // Count how many instances are selected
      for (let i = 0; i < quantity; i++) {
        const cardKey = `${cardId}-${i}`;
        if (currentSelected.has(cardKey)) {
          selectedCount++;
        }
      }
    }

    // Determine new selection state
    let newSelected: Set<string>;
    if (currentSelected === undefined) {
      // First interaction with this package
      newSelected = new Set<string>();

      if (isPackageRemoved) {
        // Package was removed (Remove All was clicked)
        // Only select the clicked card instances
        for (let i = 0; i < quantity; i++) {
          const cardKey = `${cardId}-${i}`;
          newSelected.add(cardKey);
        }
        // Clear removed packages flag since user is re-selecting
        const newRemoved = new Set(removedPackages);
        newRemoved.delete(packageKey);
        setRemovedPackages(newRemoved);
      } else {
        // Normal first interaction - select all cards except the clicked one
        const pkg = data?.packages.find(p => p.userId === packageUserId);
        if (pkg) {
          // Add all cards to selection
          pkg.cards.forEach(card => {
            for (let i = 0; i < card.quantity; i++) {
              const key = `${card.id}-${i}`;
              newSelected.add(key);
            }
          });
        }
        // Now remove all instances of the clicked card
        for (let i = 0; i < quantity; i++) {
          const cardKey = `${cardId}-${i}`;
          newSelected.delete(cardKey);
        }
      }
    } else {
      newSelected = new Set(currentSelected);

      // Toggle: if all selected -> deselect all; otherwise -> select all
      if (selectedCount === quantity) {
        // All selected -> deselect all
        for (let i = 0; i < quantity; i++) {
          const cardKey = `${cardId}-${i}`;
          newSelected.delete(cardKey);
        }
      } else {
        // Not all selected -> select all
        for (let i = 0; i < quantity; i++) {
          const cardKey = `${cardId}-${i}`;
          newSelected.add(cardKey);
        }
      }
    }

    const newMap = new Map(selectedCardsPerPackage);
    newMap.set(packageKey, newSelected);
    setSelectedCardsPerPackage(newMap);
  };

  // Handle removing all cards from a package
  const handleRemoveAllCards = (packageUserId: number) => {
    const packageKey = `pkg-${packageUserId}`;
    const newMap = new Map(selectedCardsPerPackage);
    newMap.delete(packageKey); // Clear all selections for this package
    setSelectedCardsPerPackage(newMap);

    // Add to removed packages set
    const newRemoved = new Set(removedPackages);
    newRemoved.add(packageKey);
    setRemovedPackages(newRemoved);
  };

  // Handle resetting a package (re-selecting all cards)
  const handleResetPackage = (packageUserId: number) => {
    const packageKey = `pkg-${packageUserId}`;

    // Find the package to get all card IDs
    const pkg = data?.packages.find(p => p.userId === packageUserId);
    if (pkg) {
      const allCardIds = new Set<string>();
      pkg.cards.forEach(card => {
        for (let i = 0; i < card.quantity; i++) {
          allCardIds.add(`${card.id}-${i}`);
        }
      });

      const newMap = new Map(selectedCardsPerPackage);
      newMap.set(packageKey, allCardIds);
      setSelectedCardsPerPackage(newMap);
    }

    // Remove from removed packages set
    const newRemoved = new Set(removedPackages);
    newRemoved.delete(packageKey);
    setRemovedPackages(newRemoved);
  };

  const handleImageError = (e: SyntheticEvent<HTMLImageElement>, card: any) => {
    const img = e.currentTarget;
    const currentSrc = img.src;

    // Prevent infinite loops - check if we've already tried placeholder
    if (currentSrc.includes('image-coming-soon.png')) {
      img.onerror = null;
      return;
    }

    // First try backend-provided fallback URLs if available
    const fallbacks = card?.normal_fallbacks;
    if (fallbacks && fallbacks.length > 0 && !img.dataset.fallbackAttempted) {
      img.dataset.fallbackAttempted = 'true';
      img.src = fallbacks[0];
      return;
    }

    // Try to extract Scryfall ID from CDN URL to attempt different formats
    const cdnMatch = currentSrc.match(/\/cards\/(normal|small|large)\/([a-f0-9-]+)\.jpg/);

    if (cdnMatch && !img.dataset.formatFallbackComplete) {
      const currentFormat = cdnMatch[1];
      const scryfallId = cdnMatch[2];

      // Try fallback formats: normal -> small -> large -> placeholder
      if (currentFormat === 'normal') {
        img.src = AssetUrls.cards.getCardImage(scryfallId, 'small');
        return;
      } else if (currentFormat === 'small') {
        img.src = AssetUrls.cards.getCardImage(scryfallId, 'large');
        return;
      } else if (currentFormat === 'large') {
        img.dataset.formatFallbackComplete = 'true';
      }
    }

    // If all fallbacks failed, use placeholder
    img.src = PLACEHOLDER_IMAGE;
    img.onerror = null;
    setFailedImages(prev => new Set(prev).add(card.id));
  };


  const getCurrentCards = (): TradeCard[] => {
    if (!selectedPackage) return [];

    // When auto-match is active, build a map of wantListId → sender's have list info
    // so we can default set/condition/foil to the sender's actual card
    const isAutoMatch = getPackageAutoMatchState(selectedPackage.userId);
    const haveMatchMap = isAutoMatch ? buildHaveMatchMap(selectedPackage.userId) : new Map();
    const matchPackage = matchIndexData?.packages?.[String(selectedPackage.userId)];

    // Convert API cards to TradeCard format for the modal
    return selectedPackage.cards.flatMap(card => {
      // Auto-match collapsed cards carry a list of per-copy assignments — splatter
      // each into its own modal row so the modal naturally renders one row per
      // sendable copy with the correct wantListId. This guarantees the
      // InitiateTradeRequest emits one row per assignment and routes each copy
      // to the want it was matched against.
      const assignments = (card as AutoMatchedCard)._autoMatchAssignments;
      if (assignments && assignments.length > 0) {
        return assignments.map(a => buildModalTradeCard(
          {
            ...card,
            quantity: 1,
            wantListId: a.wantListId,
            foil: a.foil,
            _autoMatchHaveCardId: String(a.cardId),
            _autoMatchCompositeId: a.compositeId,
          } as AutoMatchedCard,
          { haveCardId: String(a.cardId), condition: a.condition, foil: a.foil },
          getSetCode,
          isUUID,
        ));
      }

      // v7: Auto-match - use _autoMatchHaveCardId (from expansion) if available,
      // otherwise fall back to haveMatchMap for non-expanded cards
      const storedHaveCardId = (card as any)._autoMatchHaveCardId as string | undefined;
      const storedCompositeId = (card as any)._autoMatchCompositeId as string | undefined;

      const haveMatch: HaveMatchEntry | undefined = storedHaveCardId
        ? {
            haveCardId: storedHaveCardId,
            condition: storedCompositeId ? matchPackage?.matchDetails?.[storedCompositeId]?.condition : undefined,
            foil: storedCompositeId ? matchPackage?.matchDetails?.[storedCompositeId]?.foil : undefined,
          }
        : haveMatchMap.get(String(card.wantListId));

      return [buildModalTradeCard(
        card as AutoMatchedCard,
        haveMatch,
        getSetCode,
        isUUID,
      )];
    });
  };

  // Filter out locked packages (backend handles shipping address validation)
  const packagesWithValidAddresses = useMemo(() => {
    return data?.packages?.filter(pkg => {
      if (!pkg) return false;
      // Keep our own locked package visible
      if (pkg.isLocked && currentLockToken && selectedPackage && selectedPackage.userId === pkg.userId) {
        return true;
      }
      // Skip packages locked by others
      if (pkg.isLocked) {
        return false;
      }
      return true;
    }) || [];
  }, [data?.packages, currentLockToken, selectedPackage]);

  // Pre-process packages for both card-grid and list views
  const processedPackages: ProcessedPackage[] = useMemo(() => {
    return (packagesWithValidAddresses || []).map((pkg) => {
      // Filter out cards with 0 quantity or 0 value
      let valuedCards = pkg.cards.filter(card => {
        if (card.quantity <= 0) return false;
        const cardValue = Math.max(
          card.pointValue || 0,
          card.foilPointValue || 0,
          card.nonFoilPointValue || 0
        );
        return cardValue > 0;
      });

      const packageKey = `pkg-${pkg.userId}`;
      const isPackageRemoved = removedPackages.has(packageKey);
      const isAutoMatchActive = getPackageAutoMatchState(pkg.userId);
      const packageMatch = matchIndexData?.packages?.[String(pkg.userId)];
      const totalCardsBeforeAutoMatch = valuedCards.length;

      const hasMatchIndexData = matchIndexData && !matchIndexData.error;
      if (isAutoMatchActive && hasMatchIndexData) {
        const compositeIds = packageMatch?.matchingCardIds || [];
        valuedCards = expandAutoMatchCards(
          valuedCards,
          compositeIds,
          packageMatch?.matchDetails,
          haveListQuantityMap,
        );
      }

      // Number of physical cards sender can fulfill (deduplicated by the
      // backend per (oracleId, foil) group). matchingCardIds.length counts
      // (Want × Have) match pairs, so a single Have matching multiple Wants
      // inflates the count ("4 of 2 cards match" for 2 haves × 2 wants).
      // Fall back to matchingCardIds.length for older servers that don't
      // send matchedCardCount yet.
      const matchingCardCount = packageMatch?.matchedCardCount
        ?? packageMatch?.matchingCardIds.length
        ?? 0;
      const totalCardCount = packageMatch?.totalCards ?? totalCardsBeforeAutoMatch;

      const isEmptyDueToAutoMatch = isAutoMatchActive && !!hasMatchIndexData && valuedCards.length === 0 && totalCardsBeforeAutoMatch > 0;

      const haveMatchMap = isAutoMatchActive && hasMatchIndexData
        ? buildHaveMatchMap(pkg.userId)
        : undefined;

      return {
        pkg,
        valuedCards,
        packageKey,
        isPackageRemoved,
        isAutoMatchActive,
        isEmptyDueToAutoMatch,
        matchingCardCount,
        totalCardCount,
        haveMatchMap,
      };
    }).filter(processed => {
      // When GLOBAL auto-match is on and no cards match, hide the package entirely
      if (globalAutoMatch && processed.isEmptyDueToAutoMatch) return false;
      // Skip packages with no valued cards (unless empty due to individual auto-match)
      if (processed.valuedCards.length === 0 && !processed.isEmptyDueToAutoMatch) return false;
      return true;
    });
  }, [packagesWithValidAddresses, removedPackages, globalAutoMatch, matchIndexData, getPackageAutoMatchState, buildHaveMatchMap, haveListQuantityMap]);

  // Collect auto-match expanded rows that need printing-specific price resolution
  // Build a stable key to avoid re-fetching on every render
  const autoMatchPriceRequestKey = useMemo(() => {
    const entries: Array<{ wantListId: number; haveCardId: string; compositeId: string; condition: string; language: string; foil: boolean }> = [];
    for (const processed of processedPackages) {
      if (!processed.isAutoMatchActive) continue;
      for (const card of processed.valuedCards) {
        const haveCardId = (card as any)._autoMatchHaveCardId as string | undefined;
        const compositeId = (card as any)._autoMatchCompositeId as string | undefined;
        if (!haveCardId || !compositeId) continue;
        const matchPackage = matchIndexData?.packages?.[String(processed.pkg.userId)];
        const details = matchPackage?.matchDetails?.[compositeId];
        entries.push({
          wantListId: card.wantListId,
          haveCardId,
          compositeId,
          condition: details?.condition || card.condition || 'NM',
          language: card.language || 'English',
          foil: details?.foil ?? card.foil,
        });
      }
    }
    return entries;
  }, [processedPackages, matchIndexData]);

  // Fetch printing-specific prices for auto-match expanded rows
  useEffect(() => {
    if (autoMatchPriceRequestKey.length === 0) {
      setAutoMatchPrices(new Map());
      return;
    }

    let cancelled = false;
    const fetchPrices = async () => {
      try {
        const result = await calculatePrice({
          cards: autoMatchPriceRequestKey.map(k => ({
            wantListId: k.wantListId,
            cardId: k.haveCardId,
            condition: k.condition,
            language: k.language,
            foil: k.foil,
          }))
        }).unwrap();

        if (cancelled) return;
        const priceMap = new Map<string, number>();
        result.cards.forEach((cardPrice, index) => {
          if (index < autoMatchPriceRequestKey.length) {
            const key = autoMatchPriceRequestKey[index].compositeId;
            priceMap.set(key, cardPrice.displayPrice);
          }
        });
        setAutoMatchPrices(priceMap);
      } catch (error) {
        console.error('Failed to fetch auto-match printing prices:', error);
      }
    };

    fetchPrices();
    return () => { cancelled = true; };
  }, [autoMatchPriceRequestKey, calculatePrice]);

  // Overlay resolved printing-specific prices onto auto-match expanded card objects
  // This ensures getCardDisplayPrice (which reads card.pointValue) shows correct per-printing prices
  const processedPackagesWithPrices: ProcessedPackage[] = useMemo(() => {
    if (autoMatchPrices.size === 0) return processedPackages;

    return processedPackages.map(processed => {
      if (!processed.isAutoMatchActive) return processed;

      const updatedCards = processed.valuedCards.map(card => {
        const storedCompositeId = (card as any)._autoMatchCompositeId as string | undefined;
        if (!storedCompositeId) return card;

        const resolvedPrice = autoMatchPrices.get(storedCompositeId);
        if (resolvedPrice === undefined) return card;

        return { ...card, pointValue: resolvedPrice };
      });

      return { ...processed, valuedCards: updatedCards };
    });
  }, [processedPackages, autoMatchPrices]);

  // Reset auto-advance counter when filters change or user manually navigates
  useEffect(() => {
    setAutoAdvanceCount(0);
  }, [debouncedSearchTerm, debouncedPriceMin, debouncedPriceMax, matchingUserIds]);

  // Auto-advance past empty pages when auto-match is on
  // If the current page has 0 visible packages after filtering, skip to the next page
  useEffect(() => {
    if (!globalAutoMatch) return;
    if (isLoading || isFetching || shouldSkipPackagesQuery) return;
    if (!data || data.totalPackages === 0) return;
    if (processedPackagesWithPrices.length > 0) {
      setAutoAdvanceCount(0); // Reset when we find results
      return;
    }
    if (currentPage >= totalPages) return; // No more pages
    if (autoAdvanceCount >= 5) return; // Safety limit to prevent infinite loops

    setAutoAdvanceCount(prev => prev + 1);
    setCurrentPage(prev => prev + 1);
  }, [globalAutoMatch, processedPackagesWithPrices.length, isLoading, isFetching, shouldSkipPackagesQuery, data, currentPage, totalPages, autoAdvanceCount]);

  // Redirect away from empty/out-of-range pages (non-auto-match scenario)
  // Handles: page count reduced by filter changes, URL manipulation, client-side filtering
  useEffect(() => {
    if (isLoading || isFetching || shouldSkipPackagesQuery) return;
    if (!data) return;
    if (globalAutoMatch) return; // auto-match has its own auto-advance logic above

    // Case 1: currentPage exceeds totalPages (e.g., filters reduced results)
    if (currentPage > totalPages && totalPages >= 1) {
      setCurrentPage(totalPages);
      return;
    }

    // Case 2: Current page emptied by client-side filtering (e.g., locked packages)
    if (packagesWithValidAddresses.length === 0 && data.totalPackages > 0 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
      return;
    }
  }, [isLoading, isFetching, shouldSkipPackagesQuery, data, globalAutoMatch, currentPage, totalPages, packagesWithValidAddresses.length]);

  // Loading state - only show on initial load, not when refetching
  if (showLoadingState) {
    return (
      <div className="send-page">
        <SendPageHeader
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onSearch={setSearchQuery}
          initialSearchValue={searchQuery}
          searchType={searchType}
          onSearchTypeChange={setSearchType}
          globalAutoMatch={globalAutoMatch}
          onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
          isMatchIndexLoading={isMatchIndexLoading}
          hasMatchIndexError={!!matchIndexData?.error}
          onRetryMatchIndex={refetchMatchIndex}
          matchSummary={matchSummary}
          priceMin={priceMin}
          priceMax={priceMax}
          onPriceMinChange={setPriceMin}
          onPriceMaxChange={setPriceMax}
          sortValue={sortValue}
          onSortChange={handleSortChange}
          isSortLoading={isSortLoading}
          hasResults={false}
          selectedSets={selectedSets}
          onSelectedSetsChange={setSelectedSets}
        />
        <CardPrintingsContainer>
          <div className="send-loading-container">
            <div className="send-progress-bar">
              <div className="send-progress-fill send-progress-indeterminate" />
            </div>
            <p className="send-progress-text">
              {loadingMessage}
            </p>
          </div>
        </CardPrintingsContainer>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="send-page">
        <SendPageHeader
          displayMode={displayMode}
          onDisplayModeChange={setDisplayMode}
          onSearch={setSearchQuery}
          initialSearchValue={searchQuery}
          searchType={searchType}
          onSearchTypeChange={setSearchType}
          globalAutoMatch={globalAutoMatch}
          onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
          isMatchIndexLoading={isMatchIndexLoading}
          hasMatchIndexError={!!matchIndexData?.error}
          onRetryMatchIndex={refetchMatchIndex}
          matchSummary={matchSummary}
          priceMin={priceMin}
          priceMax={priceMax}
          onPriceMinChange={setPriceMin}
          onPriceMaxChange={setPriceMax}
          sortValue={sortValue}
          onSortChange={handleSortChange}
          isSortLoading={isSortLoading}
          hasResults={false}
          selectedSets={selectedSets}
          onSelectedSetsChange={setSelectedSets}
        />
        <CardPrintingsContainer>
          <div style={{ padding: '20px', textAlign: 'center', color: '#ff6b6b' }}>
            Error loading packages. Please try again later.
          </div>
        </CardPrintingsContainer>
      </div>
    );
  }

  // SECURITY WARNING: This is for DISPLAY ONLY
  // Backend must recalculate all prices to prevent manipulation
  const getCardDisplayPrice = (card: ApiTradeCard): number => {
    // Backend calculates pointValue based on foil status - just use it directly
    return card.pointValue;
  };

  // SECURITY WARNING: This total is for DISPLAY ONLY
  // Backend must recalculate to prevent users from manipulating totals
  const calculatePackageTotalValue = (cards: ApiTradeCard[]): number => {
    // WARNING: This can be manipulated in DevTools
    // Backend will calculate real total during trade initiation
    return cards.reduce((sum, card) => {
      const displayPrice = getCardDisplayPrice(card);
      return sum + (displayPrice * card.quantity);
    }, 0);
  };

  return (
    <div className="send-page">
      <h1 className="send-page-title">Send Cards</h1>
      <SendPageHeader
        displayMode={displayMode}
        onDisplayModeChange={setDisplayMode}
        onSearch={setSearchQuery}
        initialSearchValue={searchQuery}
        searchType={searchType}
        onSearchTypeChange={setSearchType}
        globalAutoMatch={globalAutoMatch}
        onGlobalAutoMatchToggle={handleGlobalAutoMatchToggle}
        isMatchIndexLoading={isMatchIndexLoading}
        hasMatchIndexError={!!matchIndexData?.error}
        onRetryMatchIndex={refetchMatchIndex}
        matchSummary={matchSummary}
        priceMin={priceMin}
        priceMax={priceMax}
        onPriceMinChange={setPriceMin}
        onPriceMaxChange={setPriceMax}
        sortValue={sortValue}
        onSortChange={handleSortChange}
        onExportCsv={handleExportCsv}
        isExporting={isExporting}
        hasResults={(data?.totalPackages ?? 0) > 0}
        selectedSets={selectedSets}
        onSelectedSetsChange={setSelectedSets}
      />

      {meData?.preferDomesticTrades && meData?.country && <DomesticTradesBanner />}

      <CardPrintingsContainer>
        {/* Loading overlay for pagination/search transitions */}
        {showPaginationLoading && (
          <div className="send-pagination-loading-overlay">
            <div className="send-progress-bar">
              <div className="send-progress-fill send-progress-indeterminate" />
            </div>
            <p className="send-progress-text">
              {debouncedSearchTerm ? `Searching for "${debouncedSearchTerm}"...` : `Loading page ${currentPage}...`}
            </p>
          </div>
        )}
        {/* Top Pagination — always visible when multiple pages exist */}
        {totalPages > 1 && (
          <div className="send-pagination send-pagination-top">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
              <span className="pagination-total">
                {' '}({globalAutoMatch && matchIndexData?.totalMatchingPackages != null
                  ? `${matchIndexData.totalMatchingPackages} matching users`
                  : `${data?.totalPackages || 0} users`})
              </span>
            </div>
          </div>
        )}
        {packagesWithValidAddresses.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            {searchQuery || priceMin || priceMax ? (
              <>
                <p style={{ margin: 0 }}>
                  No packages found
                  {searchQuery && ` matching "${searchQuery}"`}
                  {(priceMin || priceMax) && ` in the ${priceMin || 0} - ${priceMax || '∞'} point range`}
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setPriceMin(undefined);
                    setPriceMax(undefined);
                  }}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    border: '1px solid #666',
                    borderRadius: '4px',
                    color: '#999',
                    cursor: 'pointer'
                  }}
                >
                  Clear all filters
                </button>
              </>
            ) : (
              'No packages available at the moment. Check back later!'
            )}
          </div>
        ) : processedPackagesWithPrices.length === 0 && globalAutoMatch ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            {autoAdvanceCount >= 5 ? (
              <p>No more matching users found on remaining pages. Try turning off Auto-Match to see all available trades.</p>
            ) : (
              <>
                <span className="loading-spinner-small" />
                <p>Finding matching users...</p>
              </>
            )}
          </div>
        ) : (
          <>
          {displayMode === 'list' ? (
            <SendListView
              packages={processedPackagesWithPrices}
              selectedCardsPerPackage={selectedCardsPerPackage}
              removedPackages={removedPackages}
              onToggleAllCardInstances={handleToggleAllCardInstances}
              onSendPackage={handleSendPackage}
              onRemoveAll={handleRemoveAllCards}
              onReset={handleResetPackage}
              onAutoMatchToggle={handlePackageAutoMatchToggle}
              globalAutoMatch={globalAutoMatch}
              onCardMouseEnter={handleCardMouseEnter}
              onCardMouseLeave={handleCardMouseLeave}
              haveListQuantityByCardIdMap={haveListQuantityByCardIdMap}
            />
          ) : (
          processedPackagesWithPrices.map(({ pkg, valuedCards, packageKey, isPackageRemoved, isAutoMatchActive, isEmptyDueToAutoMatch, matchingCardCount, totalCardCount, haveMatchMap }) => {
          return (
            <CardGroupContainerResponsive
              key={pkg.userId}
              title={isAutoMatchActive
                ? `${matchingCardCount} of ${totalCardCount} cards match`
                : `${valuedCards.reduce((sum, card) => sum + card.quantity, 0)} Cards in package`}
              userName={pkg.username}
              userAvatar={cleanImageUrl(pkg.avatarUrl)}
              userCountry={pkg.country || pkg.shippingAddress?.country}
              userPoints={pkg.pointBalance}
              userIsElite={pkg.isElite}
              userShowAnimatedBorder={pkg.showAnimatedBorder}
              userBorderColor1={pkg.borderColor1}
              userBorderColor2={pkg.borderColor2}
              totalValue={calculatePackageTotalValue(valuedCards)}
              autoMatch={isAutoMatchActive}
              mode="send"
              onAutoMatchToggle={(enabled) => handlePackageAutoMatchToggle(pkg.userId, enabled)}
              autoMatchDisabled={globalAutoMatch}
              matchingCardCount={matchingCardCount}
              totalCardCount={totalCardCount}
              onSend={() => handleSendPackage({...pkg, cards: valuedCards})}
              onRemoveAll={() => handleRemoveAllCards(pkg.userId)}
              onReset={() => handleResetPackage(pkg.userId)}
              username={pkg.username}
              isRemoved={isPackageRemoved}
            >
              {/* Empty state when auto-match filters out all cards */}
              {isEmptyDueToAutoMatch ? (
                <div className="auto-match-empty-state">
                  <p className="auto-match-empty-text">
                    No cards from your Have List match this trader's wants
                  </p>
                  <button
                    className="auto-match-view-all-btn"
                    onClick={() => handlePackageAutoMatchToggle(pkg.userId, false)}
                    disabled={globalAutoMatch}
                  >
                    {globalAutoMatch ? 'Turn off global Auto-Match to view all' : 'View all cards'}
                  </button>
                </div>
              ) : (
              <div className="card-grid">
                {displayMode === 'comfortable' ? (
                  // Comfortable mode: Show individual card images for each quantity
                  [...valuedCards].sort((a, b) => {
                  // Sort by the appropriate price (prefer nonfoil if available)
                  const aPrice = getCardDisplayPrice(a);
                  const bPrice = getCardDisplayPrice(b);
                  return bPrice - aPrice;
                }).flatMap((card) => {
                  // Display the appropriate price: prefer nonfoil if available, otherwise use foil
                  const displayPrice = getCardDisplayPrice(card);

                  return Array.from({ length: card.quantity }, (_, index) => {
                    const cardKey = `${(card as any)._autoMatchCompositeId || card.id}-${index}`;
                    const selectedCards = selectedCardsPerPackage.get(packageKey);

                    // Determine if card is selected
                    let isSelected = true;

                    if (isPackageRemoved) {
                      // If package is removed, all cards are deselected
                      isSelected = false;
                    } else if (selectedCards !== undefined) {
                      // If we have interacted with this package, use the selection set
                      // (even if it's empty - empty means nothing selected)
                      isSelected = selectedCards.has(cardKey);
                    }
                    // Otherwise, no interaction yet - default to all selected (true)

                    return (
                      <div
                        key={cardKey}
                        className="card-item"
                        onClick={() => handleToggleCard(pkg.userId, cardKey)}
                        onMouseEnter={(e) => handleCardMouseEnter(e, card, pkg.username)}
                        onMouseLeave={handleCardMouseLeave}
                        style={{ cursor: 'pointer', position: 'relative' }}
                      >
                        <FoilCardImage
                          className="card-item-image"
                          src={(isAutoMatchActive ? getAutoMatchImageUrl(card, haveMatchMap) : undefined) || card.imageUrl || PLACEHOLDER_IMAGE}
                          alt={card.name}
                          foil={card.foil}
                          borderRadius="8px"
                          onError={(e) => handleImageError(e, card)}
                          loading="lazy"
                        />
                        {(failedImages.has(card.id) || !card.imageUrl) && (
                          <div className="card-name-overlay">{card.name}</div>
                        )}
                        <div className="card-item-points">
                          <Points points={displayPrice} type="small" />
                        </div>
                        {!isSelected && (
                          <div className="deselected-overlay">
                            <img
                              className="heart-icon broken"
                              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                              alt="Deselected"
                            />
                            <img
                              className="heart-icon unbroken"
                              src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/heart.svg"
                              alt="Click to select"
                            />
                          </div>
                        )}
                      </div>
                    );
                  });
                })
              ) : (
                // Compact mode: Show one card image with quantity badge
                [...valuedCards].sort((a, b) => {
                  // Sort by the appropriate price (prefer nonfoil if available)
                  const aPrice = getCardDisplayPrice(a);
                  const bPrice = getCardDisplayPrice(b);
                  return bPrice - aPrice;
                }).map((card) => {
                  // Display the appropriate price: prefer nonfoil if available, otherwise use foil
                  const displayPrice = getCardDisplayPrice(card);

                  // Check if all instances of this card are deselected
                  const selectedCards = selectedCardsPerPackage.get(packageKey);
                  let selectedCount = 0;

                  if (isPackageRemoved) {
                    // All cards are deselected
                    selectedCount = 0;
                  } else if (selectedCards === undefined) {
                    // No interaction yet - default to all cards selected
                    selectedCount = card.quantity;
                  } else {
                    // We have interacted - count selected cards (even if set is empty)
                    for (let i = 0; i < card.quantity; i++) {
                      const cardKey = `${card.id}-${i}`;
                      if (selectedCards.has(cardKey)) {
                        selectedCount++;
                      }
                    }
                  }
                  const isFullyDeselected = selectedCount === 0;

                  return (
                    <div
                      key={(card as any)._autoMatchCompositeId || card.id}
                      className="card-item"
                      onClick={() => handleToggleAllCardInstances(pkg.userId, card.id, card.quantity)}
                      onMouseEnter={(e) => handleCardMouseEnter(e, card, pkg.username)}
                      onMouseLeave={handleCardMouseLeave}
                      style={{ cursor: 'pointer', position: 'relative' }}
                    >
                      <div className="card-quantity-badge">{selectedCount > 0 ? selectedCount : card.quantity}</div>
                      <FoilCardImage
                        className="card-item-image"
                        src={(isAutoMatchActive ? getAutoMatchImageUrl(card, haveMatchMap) : undefined) || card.imageUrl || PLACEHOLDER_IMAGE}
                        alt={card.name}
                        foil={card.foil}
                        borderRadius="8px"
                        onError={(e) => handleImageError(e, card)}
                        loading="lazy"
                      />
                      {(failedImages.has(card.id) || !card.imageUrl) && (
                        <div className="card-name-overlay">{card.name}</div>
                      )}
                      <div className="card-item-points">
                        <Points points={displayPrice} type="small" />
                      </div>
                      {isFullyDeselected && (
                        <div className="deselected-overlay" style={{ opacity: 0.7 }}>
                          <img
                            className="heart-icon"
                            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/ViewCard/want-no.svg"
                            alt="All deselected"
                            style={{ width: 30, height: 30 }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
              )}
          </CardGroupContainerResponsive>
          );
        })
          )}
          </>
        )}
        {/* Global auto-match empty state when all packages are filtered out */}
        {globalAutoMatch && matchSummary?.matchingPackages === 0 && (
          <div className="auto-match-global-empty-state">
            <p>No matching packages found.</p>
            <p>Turn off Auto-Match to see all available trades.</p>
          </div>
        )}
        {/* Bottom Pagination — always visible when multiple pages exist */}
        {totalPages > 1 && (
          <div className="send-pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
              <span className="pagination-total">
                {' '}({globalAutoMatch && matchIndexData?.totalMatchingPackages != null
                  ? `${matchIndexData.totalMatchingPackages} matching users`
                  : `${data?.totalPackages || 0} users`})
              </span>
            </div>
          </div>
        )}
      </CardPrintingsContainer>

      <InitiateTradeModal
        isOpen={isModalOpen}
        cards={getCurrentCards()}
        onClose={handleCloseModal}
        onInitiateTrade={handleInitiateTrade}
        isLoading={isInitiatingTrade}
        lockId={currentLockId || undefined}
        lockExpiresAt={lockExpiresAt || undefined}
        receiverUsername={selectedPackage?.username}
        receiverAvatarUrl={cleanImageUrl(selectedPackage?.avatarUrl)}
        receiverPoints={selectedPackage?.pointBalance}
        receiverCountry={selectedPackage?.country || selectedPackage?.shippingAddress?.country}
        haveListQuantityByCardIdMap={haveListQuantityByCardIdMap}
      />

      {/* Card hover tooltip */}
      {hoveredCardData && (
        <SendCardTooltip
          cardData={hoveredCardData.data}
          position={hoveredCardData.position}
          username={hoveredCardData.username}
          onClose={handleCardMouseLeave}
          onSetHover={setHoveredSetCode}
          hoveredSetCode={hoveredSetCode}
          showImage={displayMode === 'list'}
        />
      )}
    </div>
  );
}
